import Autorenew from '@mui/icons-material/Autorenew';
import DoNotDisturbOn from '@mui/icons-material/DoNotDisturbOn';
import Refresh from '@mui/icons-material/Refresh';
import { Box, Button, CircularProgress, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { FC, useState } from 'react';

import { CustomPopover } from '@components/CustomPopover';
import { usePopover } from '@hooks/usePopover';
import { InstanceStatus, StatusInstance } from 'src/contexts/RDConnectContext';
import { useRDInstance } from 'src/hooks/useRdInstance';
import { administrationRoutes } from 'src/routes/paths';
import { RdConnectInstance } from 'src/types/apiContract';

import SelectInstallationDialog from '../Dialogs/SelectInstallationDialog';
import { sxBadge, sxIconButton, sxPopoverContent } from './styles';

const getConnectionString = (instance: RdConnectInstance) => (
  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
    Currently controlling{' '}
    <Typography component="span" variant="body1">
      <strong>{instance.name}</strong>
    </Typography>
  </Typography>
);

const getPopOverContent = (instances?: StatusInstance[] | null, myInstance?: StatusInstance | undefined) => {
  switch (true) {
    case !!myInstance?.instance && myInstance.status === InstanceStatus.ONLINE:
      return getConnectionString(myInstance.instance);

    case !!instances?.length:
      return "You're not connected to an installation of Received Connect.";

    default:
      return (
        <>
          You don’t have any active installations of Received Connect. To create and activate installations, go to{' '}
          <Link href={administrationRoutes.company('subscription')} rel="noopener">
            Company {'>'} Subscription
          </Link>
          .
        </>
      );
  }
};

const ReceivedConnectPopover: FC = () => {
  const popover = usePopover<HTMLButtonElement>();
  const [openSelect, setOpenSelect] = useState(false);

  const { instances, myConnectedInstance, refresh, isLoading } = useRDInstance();

  const showBadge = !myConnectedInstance || myConnectedInstance.status !== InstanceStatus.ONLINE;

  return (
    <>
      {openSelect && <SelectInstallationDialog onClose={() => setOpenSelect(false)} />}
      <Tooltip title="Received Connect" arrow>
        <Box component="span" position="relative" display="inline-flex">
          <IconButton onClick={popover.onOpen} sx={sxIconButton} aria-label="Received Connect">
            <Autorenew sx={{ width: 20, height: 20 }} />
          </IconButton>
          {showBadge && <DoNotDisturbOn sx={sxBadge} />}
        </Box>
      </Tooltip>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        anchorEl={popover.anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={sxPopoverContent}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
            <Typography variant="subtitle1" gutterBottom>
              Received Connect
            </Typography>
            {!!instances?.length && <Button
              variant="soft"
              size="small"
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={refresh}
              disabled={isLoading}
            >
              Refresh
            </Button>}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {getPopOverContent(instances, myConnectedInstance)}
          </Typography>
          <Button disabled={!instances?.length} variant="soft" fullWidth onClick={() => setOpenSelect(true)}>
            {myConnectedInstance?.id ? 'Change Connection' : 'Select Installation'}
          </Button>
        </Box>
      </CustomPopover>
    </>
  );
};

export default ReceivedConnectPopover;
